import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import loginHero from "@/assets/login-hero.jpg";
import { z } from "zod";
import { useAuthStore } from "@/stores/authStore";
import { GoogleCredentialResponse, GoogleLogin } from "@react-oauth/google";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // When an expired session bounced the user here, send them back to the page
  // they were on. Only same-site paths are honoured so the parameter cannot be
  // used as an open redirect.
  const returnTo = searchParams.get("returnTo");
  const destination =
    returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? returnTo
      : "/dashboard";
  const { toast } = useToast();

  const {
    login,
    loading: isLoading,
    error: authError,
    loginWithGoogle,
  } = useAuthStore();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    const result = loginSchema.safeParse({ email, password });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((error) => {
        if (error.path[0]) {
          fieldErrors[error.path[0] as string] = error.message;
        }
      });
      setFormErrors(fieldErrors);
      return;
    }

    try {
      await login({ email, password });
      toast({
        title: "Login successful!",
        description: "Welcome back to RadarTicker",
      });
      navigate(destination);
    } catch (error) {
      // Error handling is managed by Zustand state, but we can also show a toast
      const errorMessage =
        error instanceof Error ? error.message : "Invalid email or password";
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const googleLoginFailure = (response: unknown) => {
    toast({
      title: "Google Login Failed",
      description: "Something went wrong. Please try again.",
      variant: "destructive",
    });
  };

  const googleLoginSuccess = async (
    googleResponse: GoogleCredentialResponse,
  ) => {
    if (googleResponse.credential) {
      // TODO: Implement loginWithGoogle in authStore
      const response = await loginWithGoogle(googleResponse.credential);

      if (response) {
        navigate(destination);
      }
    } else {
      toast({
        title: "Google Login Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <TrendingUp className="w-8 h-8 text-success" />
            <span className="text-2xl font-bold">RadarTicker</span>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold">Welcome back</h1>
            <p className="text-muted-foreground">
              Login to your RadarTicker account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 ${formErrors.email ? "border-destructive" : ""}`}
                />
              </div>
              {formErrors.email && (
                <p className="text-sm text-destructive">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 pr-10 ${formErrors.password ? "border-destructive" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-sm text-destructive">
                  {formErrors.password}
                </p>
              )}
              {authError && (
                <p className="text-sm text-destructive mt-2">
                  {authError?.toString()}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Signing in...
                </div>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Social Login */}
          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                text="continue_with"
                containerProps={{
                  style: {
                    width: "100%",
                    margin: "auto",
                    backgroundColor: "transparent",
                  },
                }}
                onSuccess={googleLoginSuccess}
                // @ts-expect-error onFailure is not in the published prop types
                onFailure={googleLoginFailure}
              />
            </div>
          </div>

          {/* Sign Up Link */}
          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-primary font-semibold hover:underline"
            >
              Sign up
            </Link>
          </p>

          {/* Terms */}
          <p className="text-xs text-center text-muted-foreground">
            By clicking continue, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-primary">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-primary">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Right Side - Hero Image with Content */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={loginHero}
            alt="Trading Dashboard"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white">
          <div className="max-w-lg space-y-6 text-center">
            <h2 className="text-5xl font-bold leading-tight">
              Never Miss a Market Opportunity
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              Track low-volume, high-value stocks with instant alerts when
              volume surges. Monitor pre-market and after-hours trading to stay
              ahead of the curve.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-8">
              <div className="space-y-2">
                <div className="text-4xl font-bold text-success">24/7</div>
                <div className="text-sm text-white/80">Extended Hours</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-success">Real-time</div>
                <div className="text-sm text-white/80">Alerts</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-success">Custom</div>
                <div className="text-sm text-white/80">Criteria</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
